import { Edge, Node } from '@xyflow/react';

import {
  Condition,
  ConditionalStep,
  ForLoopStep,
  MatchType,
  SpecializedAgentStep,
  Step,
  StepType,
  Trigger,
  WorkflowAgent,
} from '@frontend/types/workflow-agent';

import { generateConditionNodeId } from './workflow-id-utils';

function isTriggerData(data: unknown): data is Trigger {
  return (
    data !== null &&
    typeof data === 'object' &&
    'type' in data &&
    typeof (data as { type: unknown }).type === 'string' &&
    ['webhook', 'scheduled', 'message', 'email'].includes((data as { type: string }).type)
  );
}

function isAgentStepData(
  data: unknown
): data is SpecializedAgentStep | ConditionalStep | ForLoopStep {
  return (
    data !== null &&
    typeof data === 'object' &&
    'type' in data &&
    typeof (data as { type: unknown }).type === 'string' &&
    [StepType.SPECIALIZED, StepType.CONDITIONAL, 'for_loop'].includes(
      (data as { type: string }).type
    )
  );
}

function extractTriggers(workflowNodes: WorkflowNode[]): Trigger[] {
  const triggerNodes = workflowNodes.filter((node): node is WorkflowNode & { data: Trigger } =>
    isTriggerData(node.data)
  );
  return triggerNodes.map(node => node.data);
}

type ConditionNodeData = {
  matching_condition: string;
  match_type: MatchType;
  label?: string;
};

type WorkflowNode = Node & {
  data: Step | Trigger | ConditionNodeData | ForLoopStep;
};

type WorkflowEdge = Edge & {
  label?: string;
};

export function convertToWorkflowAgent(
  nodes: Node[],
  edges: Edge[],
  baseWorkflowAgent: WorkflowAgent
): { agentData: WorkflowAgent; triggers: Trigger[] } {
  const workflowNodes: WorkflowNode[] = nodes as WorkflowNode[];
  const workflowEdges: WorkflowEdge[] = edges as WorkflowEdge[];

  // Extract triggers
  const triggers = extractTriggers(workflowNodes);

  // Process top-level nodes (not nested)
  const topLevelNodes = workflowNodes.filter(node => !node.parentId);
  const steps: Record<string, Step> = {};

  // Process each top-level node
  topLevelNodes.forEach(node => {
    if (isAgentStepData(node.data)) {
      if (node.data.type === 'for_loop') {
        steps[node.id] = processForLoopNode(node, workflowNodes, workflowEdges);
      } else if (node.data.type === StepType.CONDITIONAL) {
        steps[node.id] = processConditionalNode(node, workflowNodes, workflowEdges);
      } else if (node.data.type === StepType.SPECIALIZED) {
        steps[node.id] = processAgentStep(node);
      }
    }
  });

  // Set next_step_id relationships based on edges
  setNextStepRelationships(steps, workflowEdges, workflowNodes);

  // Find start step
  const startStepId = findStartStep(topLevelNodes, workflowEdges);

  // Create metadata with nodes, updating condition node IDs to be deterministic and trigger node IDs to match current triggers
  const metadataNodes = nodes.map(node => {
    let nodeId = node.id;

    if (node.type === 'condition') {
      const incomingEdge = workflowEdges.find(edge => edge.target === node.id);
      if (incomingEdge) {
        const parentNode = workflowNodes.find(n => n.id === incomingEdge.source);
        if (parentNode && parentNode.data.type === StepType.CONDITIONAL) {
          const conditionData = node.data as ConditionNodeData;
          if (
            conditionData.matching_condition !== undefined &&
            conditionData.match_type !== undefined
          ) {
            nodeId = generateConditionNodeId(parentNode.id, {
              matching_condition: conditionData.matching_condition,
              match_type: conditionData.match_type,
            });
          }
        }
      }
    } else if (isTriggerData(node.data)) {
      const matchingTrigger = triggers.find(
        trigger =>
          trigger.type === node.data.type && JSON.stringify(trigger) === JSON.stringify(node.data)
      );
      if (matchingTrigger) {
        nodeId = matchingTrigger.id;
      }
    }

    return {
      id: nodeId,
      position: node.position,
      measured: node.measured,
    };
  });

  const metadataEdges = edges.map(edge => ({
    id: edge.id,
    source: edge.source,
    target: edge.target,
    sourceHandle: edge.sourceHandle,
    targetHandle: edge.targetHandle,
  }));

  const metadata = {
    nodes: metadataNodes,
    edges: metadataEdges,
  };

  // Create agent data without nodes and edges, but with metadata
  const { nodes: _nodes, edges: _edges, ...baseWorkflowAgentWithoutGraph } = baseWorkflowAgent;
  void _nodes;
  void _edges;

  const agentData = {
    ...baseWorkflowAgentWithoutGraph,
    steps,
    start_step_id: startStepId,
    agent_metadata: metadata,
  };

  return { agentData, triggers };
}

/**
 * Recursively process a ForLoop node and all its children
 */
function processForLoopNode(
  forLoopNode: WorkflowNode,
  allNodes: WorkflowNode[],
  allEdges: WorkflowEdge[]
): ForLoopStep {
  const forLoopData = forLoopNode.data as ForLoopStep;

  // Find all direct children of this ForLoop
  const childNodes = allNodes.filter(node => node.parentId === forLoopNode.id);

  const forLoopSteps: Record<string, Step> = {};

  // Process each child node recursively
  childNodes.forEach(childNode => {
    if (isAgentStepData(childNode.data)) {
      if (childNode.data.type === 'for_loop') {
        // Recursive case: nested ForLoop
        forLoopSteps[childNode.id] = processForLoopNode(childNode, allNodes, allEdges);
      } else if (childNode.data.type === StepType.CONDITIONAL) {
        forLoopSteps[childNode.id] = processConditionalNode(childNode, allNodes, allEdges);
      } else if (childNode.data.type === StepType.SPECIALIZED) {
        forLoopSteps[childNode.id] = processAgentStep(childNode);
      }
    }
  });

  // Set internal step relationships for this ForLoop
  setNextStepRelationshipsInContainer(forLoopSteps, allEdges, childNodes);

  return {
    step_id: forLoopNode.id,
    type: StepType.FOR_LOOP,
    name: forLoopData.name || 'For Loop',
    description: forLoopData.description || '',
    items_source: forLoopData.items_source || '$WORKFLOW_INPUT.items',
    item_name: forLoopData.item_name || 'item',
    max_iterations: forLoopData.max_iterations || null,
    parallel: forLoopData.parallel || false,
    steps: forLoopSteps,
    next_step_id: null, // Will be set by parent processing
    parameters: forLoopData.parameters || {},
    timeout_seconds: forLoopData.timeout_seconds || 3600,
  };
}

/**
 * Process a conditional node and consolidate connected condition nodes
 */
function processConditionalNode(
  conditionalNode: WorkflowNode,
  allNodes: WorkflowNode[],
  allEdges: WorkflowEdge[]
): ConditionalStep {
  const conditionalData = conditionalNode.data as ConditionalStep;

  // Find all condition nodes connected to this conditional node
  const conditionNodes = findConnectedConditionNodes(conditionalNode, allNodes, allEdges);

  const conditions: Condition[] = [];
  conditionNodes.forEach(conditionNode => {
    const conditionData = conditionNode.data as ConditionNodeData;
    // Find where this condition node leads to
    const outgoingEdge = allEdges.find(edge => edge.source === conditionNode.id);
    if (outgoingEdge) {
      conditions.push({
        matching_condition: conditionData.matching_condition || '',
        match_type: conditionData.match_type || MatchType.EQUALS,
        go_to_step_id: outgoingEdge.target,
      });
    }
  });

  return {
    step_id: conditionalNode.id,
    type: StepType.CONDITIONAL,
    name: conditionalData.name || 'Conditional',
    description: conditionalData.description || '',
    evaluator_agent_id: conditionalData.evaluator_agent_id || conditionalNode.id,
    conditions: conditions.length > 0 ? conditions : conditionalData.conditions || [],
    next_step_id: null, // Will be set by edge processing
    parameters: conditionalData.parameters || {},
    timeout_seconds: conditionalData.timeout_seconds || 3600,
  };
}

/**
 * Process a simple agent step
 */
function processAgentStep(agentNode: WorkflowNode): SpecializedAgentStep {
  const agentData = agentNode.data as SpecializedAgentStep;

  return {
    step_id: agentNode.id,
    type: StepType.SPECIALIZED,
    name: agentData.name || 'Agent',
    description: agentData.description || '',
    agent_id: agentData.agent_id || agentNode.id,
    next_step_id: null, // Will be set by edge processing
    parameters: agentData.parameters || {},
    timeout_seconds: agentData.timeout_seconds || 3600,
  };
}

/**
 * Set next_step_id relationships based on edges
 */
function setNextStepRelationships(
  steps: Record<string, Step>,
  edges: WorkflowEdge[],
  allNodes: WorkflowNode[]
): void {
  edges.forEach(edge => {
    const sourceNode = allNodes.find(n => n.id === edge.source);
    const targetNode = allNodes.find(n => n.id === edge.target);

    // Skip if source or target is missing, or if target is a condition node
    if (!sourceNode || !targetNode || targetNode.type === 'condition') {
      return;
    }

    // Only set relationships for top-level steps (not nested)
    if (!sourceNode.parentId && !targetNode.parentId && steps[edge.source]) {
      steps[edge.source].next_step_id = edge.target;
    }
  });
}

/**
 * Set next_step_id relationships within a container (ForLoop)
 */
function setNextStepRelationshipsInContainer(
  containerSteps: Record<string, Step>,
  edges: WorkflowEdge[],
  containerNodes: WorkflowNode[]
): void {
  const containerNodeIds = new Set(containerNodes.map(node => node.id));

  edges.forEach(edge => {
    // Only process edges between nodes in this container
    if (containerNodeIds.has(edge.source) && containerNodeIds.has(edge.target)) {
      const targetNode = containerNodes.find(n => n.id === edge.target);
      // Skip if target is a condition node
      if (targetNode && targetNode.type !== 'condition' && containerSteps[edge.source]) {
        containerSteps[edge.source].next_step_id = edge.target;
      }
    }
  });
}

/**
 * Find start step (node with no incoming edges)
 */
function findStartStep(nodes: WorkflowNode[], edges: WorkflowEdge[]): string {
  const nodeIds = new Set(nodes.map(node => node.id));

  for (const node of nodes) {
    if (isAgentStepData(node.data)) {
      // Check if this node has incoming edges from other nodes at the same level
      const hasIncomingEdge = edges.some(
        edge => edge.target === node.id && nodeIds.has(edge.source)
      );

      if (!hasIncomingEdge) {
        return node.id;
      }
    }
  }

  // Fallback to first agent node
  const firstAgentNode = nodes.find(node => isAgentStepData(node.data));
  return firstAgentNode?.id || '';
}

/**
 * Find condition nodes connected to a conditional step
 */
function findConnectedConditionNodes(
  conditionalNode: WorkflowNode,
  allNodes: WorkflowNode[],
  allEdges: WorkflowEdge[]
): WorkflowNode[] {
  const conditionNodes: WorkflowNode[] = [];

  // Find all outgoing edges from the conditional node
  const outgoingEdges = allEdges.filter(edge => edge.source === conditionalNode.id);

  outgoingEdges.forEach(edge => {
    const targetNode = allNodes.find(node => node.id === edge.target);
    if (targetNode && targetNode.type === 'condition') {
      conditionNodes.push(targetNode);
    }
  });

  return conditionNodes;
}
